import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum, fmtDate, API } from '@/lib/api';
import { Loading, EmptyState, SectionTitle, StatusBadge, PriorityBadge } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Download, Search, Plus, Loader2, CalendarClock, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { logError } from '@/lib/log';

const SLOTS = [
  { label: '06:00 AM - 09:00 AM', h: 6 },
  { label: '09:00 AM - 12:00 PM', h: 9 },
  { label: '12:00 PM - 03:00 PM', h: 12 },
  { label: '03:00 PM - 09:00 PM', h: 15 },
  { label: '09:00 PM - 12:00 AM', h: 21 },
  { label: '12:00 AM - 06:00 AM', h: 0 },
];

export default function AdminOrders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('date');
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    const params = [];
    if (status !== 'all') params.push(`status=${status}`);
    if (sort !== 'date') params.push(`sort=${sort}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    api.get(`/orders${qs}`).then((r) => setOrders(r.data)).catch((err) => logError('Failed to load orders:', err));
  }, [status, sort]);
  useEffect(() => { load(); }, [load]);

  if (!orders) return <Loading />;
  const list = orders.filter((o) => !q || (o.order_no + o.customer_name).toLowerCase().includes(q.toLowerCase()));
  const statuses = ['all', 'placed', 'confirmed', 'driver_assigned', 'en_route', 'arrived', 'delivery_started', 'completed', 'invoice_generated', 'cancelled'];

  return (
    <div className="space-y-4">
      <SectionTitle action={
        <div className="flex gap-2">
          <NewOrderDialog onCreated={load} />
          <Button variant="secondary" size="sm" onClick={() => window.open(`${API}/admin/reports/export?kind=orders`, '_blank')} data-testid="orders-export-button"><Download className="h-4 w-4 mr-1" />Export</Button>
        </div>
      }>Order Management</SectionTitle>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order or customer" className="pl-9" data-testid="orders-search-input" /></div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="md:w-52" data-testid="admin-orders-filter-status-select"><SelectValue /></SelectTrigger><SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s === 'all' ? 'All Statuses' : s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
        <Select value={sort} onValueChange={setSort}><SelectTrigger className="md:w-52" data-testid="admin-orders-sort-select"><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="date">Sort: Newest first</SelectItem>
          <SelectItem value="urgency">Sort: Priority / Urgency</SelectItem>
          <SelectItem value="distance">Sort: Nearest distance</SelectItem>
        </SelectContent></Select>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60"><tr className="text-left"><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Priority</th><th className="p-3">Schedule</th><th className="p-3">Distance</th><th className="p-3">Qty</th><th className="p-3">Total</th><th className="p-3">Driver</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.id} className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => nav(`/admin/orders/${o.id}`)} data-testid={`admin-order-row-${o.order_no}`}>
                <td className="p-3 font-medium">{o.order_no}</td>
                <td className="p-3">{o.customer_name}</td>
                <td className="p-3"><PriorityBadge priority={o.priority} /></td>
                <td className="p-3 text-xs text-muted-foreground">{o.schedule_type === 'scheduled' ? (o.delivery_time || 'Scheduled') : 'ASAP'}</td>
                <td className="p-3 tabular text-muted-foreground">{o.distance_km != null ? `${o.distance_km} km` : '-'}</td>
                <td className="p-3 tabular">{fmtNum(o.pricing.qty)} {o.pricing.unit}</td>
                <td className="p-3 tabular">{fmtINR(o.pricing.total)}</td>
                <td className="p-3">{o.driver_name || '-'}</td>
                <td className="p-3"><StatusBadge status={o.status} label={o.status_label} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="p-6"><EmptyState title="No orders found" /></div>}
      </div>
    </div>
  );
}

const NewOrderDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState('');
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('1000');
  const [scheduleType, setScheduleType] = useState('asap');
  const [schedDate, setSchedDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [schedSlot, setSchedSlot] = useState('9');
  const [priority, setPriority] = useState('normal');
  const [payment, setPayment] = useState('Credit');
  const [instructions, setInstructions] = useState('');
  const [slotAvail, setSlotAvail] = useState(null);

  const slotInfo = (h) => {
    const s = slotAvail?.slots?.[String(h)];
    if (s) return { remaining: s.remaining, full: s.full, allowed: s.allowed, reason: s.reason };
    const cap = slotAvail?.capacity ?? 3;
    return { remaining: cap, full: false, allowed: true, reason: null };
  };

  useEffect(() => {
    if (!open || scheduleType !== 'scheduled' || !schedDate) { setSlotAvail(null); return; }
    api.get(`/orders/slot-availability?date=${schedDate}`)
      .then((r) => setSlotAvail(r.data))
      .catch(() => setSlotAvail({ capacity: 3, counts: {} }));
  }, [open, scheduleType, schedDate]);

  useEffect(() => {
    if (scheduleType !== 'scheduled' || !slotAvail) return;
    if (slotInfo(Number(schedSlot)).full) {
      const openSlot = SLOTS.find((s) => !slotInfo(s.h).full);
      if (openSlot) setSchedSlot(String(openSlot.h));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotAvail]);

  useEffect(() => {
    if (open) {
      api.get('/customers?company=all').then((r) => setCustomers(r.data)).catch(() => {});
      api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
    }
  }, [open]);

  const onCustomer = async (id) => {
    setCustomerId(id); setAddresses([]); setAddressId('');
    try {
      const r = await api.get(`/customers/${id}`);
      const addrs = r.data.addresses || [];
      setAddresses(addrs);
      const def = addrs.find((a) => a.is_default) || addrs[0];
      if (def) setAddressId(def.id);
    } catch (err) { logError('Failed to load customer addresses:', err); setAddresses([]); }
  };

  const create = async () => {
    if (!customerId) return toast.error('Select a customer');
    if (!productId) return toast.error('Select a product');
    setBusy(true);
    try {
      const slot = SLOTS.find((s) => String(s.h) === schedSlot) || SLOTS[1];
      let scheduledAt = null, label = 'ASAP (Deliver Now)';
      if (scheduleType === 'scheduled') {
        const dt = new Date(`${schedDate}T${String(slot.h).padStart(2, '0')}:00:00`);
        scheduledAt = dt.toISOString();
        label = `${dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${slot.label}`;
      }
      await api.post('/orders/admin', {
        customer_id: customerId, product_id: productId, qty: Number(qty), address_id: addressId || null,
        delivery_time: label, schedule_type: scheduleType, scheduled_at: scheduledAt,
        slot_date: scheduleType === 'scheduled' ? schedDate : null,
        slot_hour: scheduleType === 'scheduled' ? slot.h : null,
        priority, payment_method: payment, instructions,
      });
      toast.success('Order created');
      setOpen(false);
      setCustomerId(''); setProductId(''); setQty('1000'); setScheduleType('asap'); setPriority('normal'); setInstructions('');
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create order'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="admin-new-order-button"><Plus className="h-4 w-4 mr-1" />New Order</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Order (for customer)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={onCustomer}>
              <SelectTrigger data-testid="new-order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Delivery Address</Label>
            {addresses.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No saved address — order will use the depot location by default.</p>
            ) : (
              <Select value={addressId} onValueChange={setAddressId}>
                <SelectTrigger data-testid="new-order-address-select"><SelectValue placeholder="Select address" /></SelectTrigger>
                <SelectContent>{addresses.map((a) => <SelectItem key={a.id} value={a.id}>{a.label} — {a.address}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Product</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger data-testid="new-order-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantity</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="new-order-qty-input" /></div>
          </div>

          <div>
            <Label className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Delivery Schedule</Label>
            <RadioGroup value={scheduleType} onValueChange={setScheduleType} className="grid grid-cols-2 gap-2 mt-2">
              {[{ v: 'asap', t: 'Deliver ASAP' }, { v: 'scheduled', t: 'Schedule for later' }].map((o) => (
                <label key={o.v} className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer ${scheduleType === o.v ? 'border-primary bg-muted' : 'border-border'}`}>
                  <RadioGroupItem value={o.v} data-testid={`new-order-schedule-${o.v}`} /><span className="text-sm">{o.t}</span>
                </label>
              ))}
            </RadioGroup>
            {scheduleType === 'scheduled' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div><Label className="text-xs">Date</Label><Input type="date" min={new Date().toISOString().slice(0, 10)} value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="new-order-date-input" /></div>
                <div><Label className="text-xs">Time Slot</Label>
                  <Select value={schedSlot} onValueChange={setSchedSlot}>
                    <SelectTrigger data-testid="new-order-slot-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SLOTS.map((s) => { const info = slotInfo(s.h); return (
                      <SelectItem key={s.h} value={String(s.h)} disabled={info.full} data-testid={`admin-slot-option-${s.h}`}>
                        {s.label} {info.full ? '— Full' : `— ${info.remaining} left`}
                      </SelectItem>
                    ); })}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="new-order-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{['urgent', 'high', 'normal', 'low'].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger data-testid="new-order-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>{['Credit', 'UPI', 'Net Banking', 'Corporate Billing'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Instructions</Label><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Optional notes for driver" data-testid="new-order-instructions-input" /></div>
        </div>
        <DialogFooter><Button onClick={create} disabled={busy} data-testid="new-order-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Order'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
